import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Patch, Post } from "@repo/utils/api";
import Compare from "@repo/utils/Compare";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { api } from "..";

import { Organization } from "./organizations";
import { Staff, useStaffs } from "./staffs";
import { renderToStaticMarkup } from "@usewaypoint/email-builder";
import UString from "@repo/utils/UString";
import UZod from "@repo/utils/UZod";
import USolid from "@repo/utils-solid/USolid";
import Base64 from "@repo/utils/Base64";
import UProxy from "@repo/utils/UProxy";

export type EmailTemplate = {
  id: EmailTemplate.Id;
  organizationId: Organization.Id;

  name: string;
  readonlyName?: boolean;
  modifiedAt: Date;
  savedAt?: Date;
  lastEditorId: Staff.Id;
};

export namespace EmailTemplate {
  export type Id = `et_${string}`;
  export namespace Id {
    export const is = (id: string | undefined): id is Id =>
      !!id?.startsWith("et_");
    export const zodSchema = (message = "Invalid email template id") =>
      UZod.predicate(is, message);
  }

  export type Params = {
    name: string;
    description?: string;
    contentJson: string;
  };
  export namespace Params {
    export type Update = Partial<Params> &
      (
        | {
            save: true;
            raw?: boolean;
            changeDescription: string;
            contentHtml: string;
          }
        | {}
      );
  }

  export type Version = {
    id: Version.Id;
    contentJson?: string;
    contentHtml?: string;
    savedAt: Date;
    lastEditorId: Staff.Id;
    changeDescription: string;
  };
  export namespace Version {
    export type Id = `etv_${string}`;

    export type Preview = Omit<Version, "contentJson">;
  }

  export type Transactional = {
    id: Transactional.Id;
    emailTemplateId: EmailTemplate.Id;

    transactionName: string;
    title: string;
    description: string;
    subject: string;
    enabled: boolean;
    variables: Record<string, string>;
  };
  export namespace Transactional {
    export type Id = `tt_${string}`;
    export type Params = {
      enabled: boolean;
      subject: string;
    };
  }
}

export type EmailTemplatesRest = {
  "email-templates": {
    [id: EmailTemplate.Id]: Get<{}, { emailTemplate: EmailTemplate }> &
      Patch<
        EmailTemplate.Params.Update,
        {
          emailTemplate: EmailTemplate;
          draft: EmailTemplate.Version;
          savedVersion?: EmailTemplate.Version;
        }
      > &
      Delete & {
        versions: Get<
          {},
          { emailTemplateVersions: EmailTemplate.Version.Preview[] }
        > & {
          [id in EmailTemplate.Version.Id | "draft" | "saved"]: Get<
            {},
            {
              // Can be nullish if saved version does not exist
              emailTemplateVersion?: EmailTemplate.Version;
            }
          >;
        };
      } & {
        images: Post<
          {
            imgBase64: Base64;
            imgMimeType: string;
          },
          { imgUrl: string }
        >;
      };
  };

  "transactional-templates": {
    [id: EmailTemplate.Transactional.Id]: Get<
      {},
      { transactionalTemplate: EmailTemplate.Transactional }
    > &
      Patch<
        Partial<EmailTemplate.Transactional.Params>,
        { transactionalTemplate: EmailTemplate.Transactional }
      >;
  };

  "organizations": {
    [id: Organization.Id]: {
      "email-templates": Get<
        {},
        { emailTemplates: EmailTemplate[]; staffProfiles: Staff[] }
      > &
        Post<
          Pick<EmailTemplate.Params, "name">,
          { emailTemplate: EmailTemplate; lastEditor: Staff }
        >;
      "transactional-templates": Get<
        {},
        {
          transactionalTemplates: EmailTemplate.Transactional[];
          emailTemplates: EmailTemplate[];
          staffProfiles: Staff[];
        }
      >;
    };
  };
};

const initialState = {
  byId: {} as Record<EmailTemplate.Id, EmailTemplate>,
  versionById: {} as Record<EmailTemplate.Version.Id, EmailTemplate.Version>,
  draftById: {} as Record<EmailTemplate.Id, EmailTemplate.Version.Id>,
  savedById: {} as Record<EmailTemplate.Id, EmailTemplate.Version.Id>,
  transactionalById: {} as Record<
    EmailTemplate.Transactional.Id,
    EmailTemplate.Transactional
  >,
  forOrganization: {} as Record<
    Organization.Id,
    {
      marketing?: EmailTemplate.Id[];
      transactional?: EmailTemplate.Transactional.Id[];
    }
  >,
};

export const { Context, Provider, useEmailTemplates } = FeatureStore.init(
  "EmailTemplates",
  initialState,
  ([store, setStore], { wrapActions, attachAction }) => {
    const [, setStaffs] = useStaffs();

    const accessors = {
      byId: attachAction(
        (id: EmailTemplate.Id) =>
          store.byId[id] &&
          UProxy.merge(store.byId[id], {
            get savedVersion() {
              return accessors.version(id, "saved");
            },
            get draftVersion() {
              return accessors.version(id, "draft");
            },
          }),
        (id) => actions.fetch(id),
      ),
      version: attachAction(
        (
          templateId: EmailTemplate.Id,
          versionId: EmailTemplate.Version.Id | "draft" | "saved",
        ) => {
          if (versionId === "draft")
            return store.versionById[store.draftById[templateId]!];
          if (versionId === "saved")
            return store.versionById[store.savedById[templateId]!];
          return store.versionById[versionId];
        },
        (templateId, versionId) => actions.fetchVersion(templateId, versionId),
      ),

      transactional: attachAction(
        (id: EmailTemplate.Transactional.Id) =>
          store.transactionalById[id] &&
          UProxy.merge(store.transactionalById[id], {
            get emailTemplate() {
              return accessors.byId(
                store.transactionalById[id]!.emailTemplateId,
              );
            },
          }),
        (id) => actions.fetchTransactional(id),
      ),

      forOrganization: {
        marketing: attachAction(
          (id: Organization.Id) =>
            store.forOrganization[id]?.marketing?.map((id) => store.byId[id]!),
          (id) => actions.forOrganization.marketing.fetch(id),
        ),
        transactional: attachAction(
          (id: Organization.Id) =>
            store.forOrganization[id]?.transactional?.map(
              (id) => store.transactionalById[id]!,
            ),
          (id) => actions.forOrganization.transactional.fetch(id),
        ),
      },
    };

    const mutations = {
      one(template: EmailTemplate) {
        setStore("byId", template.id, reconcile(template));
      },
      many(templates: EmailTemplate[]) {
        for (const template of templates) {
          mutations.one(template);
        }
      },
      oneVersion(version: EmailTemplate.Version) {
        setStore("versionById", version.id, reconcile(version));
      },
      manyVersions(versions: EmailTemplate.Version[]) {
        for (const version of versions) {
          mutations.oneVersion(version);
        }
      },
      oneTransactional(transactional: EmailTemplate.Transactional) {
        setStore(
          "transactionalById",
          transactional.id,
          reconcile(transactional),
        );
      },
      manyTransactionals(transactionals: EmailTemplate.Transactional[]) {
        for (const transactional of transactionals) {
          mutations.oneTransactional(transactional);
        }
      },
      forOrganization: {
        marketing(id: Organization.Id, templates: EmailTemplate[]) {
          batch(() => {
            mutations.many(templates);
            setStore("forOrganization", id, {
              marketing: templates
                .sort(
                  Compare.comparator(({ modifiedAt }) => -modifiedAt.getTime()),
                )
                .map(({ id }) => id),
            });
          });
        },
        transactional(
          id: Organization.Id,
          templates: EmailTemplate.Transactional[],
        ) {
          batch(() => {
            mutations.manyTransactionals(templates);
            setStore("forOrganization", id, {
              transactional: templates.map(({ id }) => id),
            });
          });
        },
      },
    };

    const actions = wrapActions({
      many: mutations.many,
      manyVersions: mutations.manyVersions,
      fetch(id: EmailTemplate.Id) {
        return api["email-templates"][id]!.GET({}).then(({ emailTemplate }) => {
          mutations.one(emailTemplate);
        });
      },
      fetchVersion(
        id: EmailTemplate.Id,
        versionId: EmailTemplate.Version.Id | "draft" | "saved",
      ) {
        return api["email-templates"]
          [id]!.versions[versionId]!.GET({})
          .then(({ emailTemplateVersion }) => {
            if (!emailTemplateVersion) return;
            batch(() => {
              mutations.oneVersion(emailTemplateVersion);
              if (!UString.startsWith(versionId, "etv_"))
                setStore(`${versionId}ById`, id, emailTemplateVersion.id);
            });
            return emailTemplateVersion;
          });
      },
      fetchTransactional(id: EmailTemplate.Transactional.Id) {
        return api["transactional-templates"]
          [id]!.GET({})
          .then(({ transactionalTemplate }) => {
            mutations.oneTransactional(transactionalTemplate);
          });
      },

      rename(id: EmailTemplate.Id, name: string) {
        return api["email-templates"]
          [id]!.PATCH({ name })
          .then(({ emailTemplate }) => {
            mutations.one(emailTemplate);
          });
      },

      contentJson(id: EmailTemplate.Id, contentJson: string) {
        if (contentJson === accessors.version(id, "draft")?.contentJson)
          return Promise.resolve();
        return api["email-templates"]
          [id]!.PATCH({ contentJson })
          .then(({ emailTemplate, draft }) => {
            batch(() => {
              mutations.one(emailTemplate);
              mutations.oneVersion(draft);
            });
          });
      },

      save: (() => {
        const base = async (
          id: EmailTemplate.Id,
          params: Partial<EmailTemplate.Params.Update>,
        ) =>
          api["email-templates"]
            [id]!.PATCH({
              save: true,
              ...params,
            })
            .then(({ emailTemplate, draft, savedVersion }) => {
              batch(() => {
                mutations.one(emailTemplate);
                mutations.oneVersion(draft);
                setStore("draftById", id, draft.id);
                if (savedVersion) {
                  mutations.oneVersion(savedVersion);
                  setStore("savedById", id, savedVersion.id);
                }
              });
            });

        return Object.assign(
          async (id: EmailTemplate.Id, changeDescription: string) => {
            if (!accessors.version(id, "draft"))
              throw new Error("Must load draft before saving.");

            // Prevent saving before contentJson is applied by debounced EmailBuilder updates
            // TODO: Optimistically update contentJson?
            const draft = await USolid.Accessor.matchValue(
              () => accessors.version(id, "draft"),
              (draft): draft is EmailTemplate.Version => !!draft!.contentJson,
            );

            return base(id, {
              changeDescription,
              contentJson: draft.contentJson,
              contentHtml: renderToStaticMarkup(
                JSON.parse(draft.contentJson!),
                {
                  rootBlockId: "root",
                },
              ),
            });
          },
          {
            raw: (
              id: EmailTemplate.Id,
              changeDescription: string,
              rawHtml: string,
            ) =>
              base(id, {
                changeDescription,
                raw: true,
                contentHtml: rawHtml,
              }),
          },
        );
      })(),

      delete(orgId: Organization.Id, id: EmailTemplate.Id) {
        return api["email-templates"][id]!.DELETE({}).then(() => {
          setStore("forOrganization", orgId, "marketing", (ids) =>
            ids!.filter((t) => t !== id),
          );
        });
      },

      uploadImage(
        id: EmailTemplate.Id,
        img: { base64: Base64; mimeType: string },
      ) {
        return api["email-templates"]
          [id]!.images.POST({
            imgBase64: img.base64,
            imgMimeType: img.mimeType,
          })
          .then(({ imgUrl }) => imgUrl);
      },

      transactional: {
        update(
          id: EmailTemplate.Transactional.Id,
          params: Partial<EmailTemplate.Transactional.Params>,
        ) {
          return api["transactional-templates"]
            [id]!.PATCH(params)
            .then(({ transactionalTemplate }) => {
              mutations.oneTransactional(transactionalTemplate);
              return transactionalTemplate;
            });
        },
      },

      forOrganization: {
        marketing: {
          fetch(id: Organization.Id) {
            return api.organizations[id]!["email-templates"]!.GET({}).then(
              ({ emailTemplates, staffProfiles }) => {
                batch(() => {
                  mutations.forOrganization.marketing(id, emailTemplates);
                  setStaffs.many(staffProfiles);
                });
              },
            );
          },
          create(id: Organization.Id, name: string) {
            return api.organizations[id]!["email-templates"]!.POST({
              name,
            }).then(({ emailTemplate, lastEditor }) => {
              batch(() => {
                setStaffs.one(lastEditor);
                mutations.forOrganization.marketing(id, [
                  ...(accessors.forOrganization.marketing(id) || []),
                  emailTemplate,
                ]);
              });
              return emailTemplate;
            });
          },
        },
        transactional: {
          fetch(id: Organization.Id) {
            return api.organizations[id]!["transactional-templates"]!.GET(
              {},
            ).then(
              ({ transactionalTemplates, emailTemplates, staffProfiles }) => {
                batch(() => {
                  mutations.forOrganization.transactional(
                    id,
                    transactionalTemplates,
                  );
                  const transactionalByEmailTemplateId = Object.fromEntries(
                    transactionalTemplates.map(({ emailTemplateId }) => [
                      emailTemplateId,
                      transactionalTemplates.find(
                        ({ emailTemplateId }) =>
                          emailTemplateId === emailTemplateId,
                      ),
                    ]),
                  );
                  mutations.many(
                    emailTemplates.map((emailTemplate) => ({
                      ...emailTemplate,
                      name: transactionalByEmailTemplateId[emailTemplate.id]!
                        .title,
                      readonlyName: true,
                    })),
                  );
                  setStaffs.many(staffProfiles);
                });
              },
            );
          },
        },
      },
    });

    return { accessors, actions };
  },
);
